import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  Clapperboard,
  Download,
  FolderInput,
  FolderPlus,
  Loader2,
  MoreVertical,
  Pencil,
  Play,
  Search,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { RecordingSummary } from "@rec/shared";
import { i18n, useServerText } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "@/components/Toaster";
import { FolderTree } from "@/components/FolderTree";
import { MediaCard } from "@/components/MediaCard";
import { UploadButton } from "@/components/UploadButton";
import { ImportLinkButton } from "@/components/ImportLinkDialog";
import { statusExplanation } from "@/components/RecordingStatus";
import { DeleteImpact } from "@/components/DeleteImpact";
import { Button, EmptyState, ErrorState, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * The library: every video the workspace has, as a browsable grid.
 *
 * This is the media home — uploads land here, Studio imports from here, and
 * managing (rename, move, delete, download) happens here. It deliberately
 * shows *all* videos: bot recordings, uploads and published edits are one
 * collection, because to the person editing they are all just footage.
 *
 * The meetings page keeps its list view: that page answers "what happened in
 * my meetings", this one answers "what footage do I have".
 */

type SortKey = "newest" | "oldest" | "longest" | "name";

/** Labels are `library:sort.<key>`, translated when rendered. */
const SORT_KEYS: SortKey[] = ["newest", "oldest", "longest", "name"];

export function MediaLibrary() {
  const { t } = useTranslation(["library", "common"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ id: string; label: string } | null>(null);

  const folders = useQuery({ queryKey: ["projects"], queryFn: api.listProjects });

  const recordings = useQuery({
    queryKey: ["recordings"],
    queryFn: api.listRecordings,
    // Fresh uploads and live bots keep moving; the grid follows on its own.
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status !== "ready" && r.status !== "failed")
        ? 10_000
        : false,
  });

  // Any click outside a menu closes it. The menus stopPropagation themselves.
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [menuFor]);

  const moveRecording = useMutation({
    mutationFn: (input: { id: string; projectId: string | null }) =>
      api.moveRecording(input.id, input.projectId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const renameRecording = useMutation({
    mutationFn: (input: { id: string; title: string }) =>
      api.renameRecording(input.id, input.title),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["recordings"] }),
  });

  const deleteRecording = useMutation({
    mutationFn: (id: string) => api.deleteRecording(id),
    onSuccess: () => {
      toast(i18n.t("library:menu.deleted"), "success");
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const editInStudio = useMutation({
    mutationFn: (id: string) => api.createStudioProject({ sourceRecordingId: id }),
    onSuccess: (project) => {
      void navigate({ to: "/studio/$projectId", params: { projectId: project.id } });
    },
  });

  const download = useMutation({
    mutationFn: (id: string) => api.getDownloadUrl(id),
    onSuccess: (url) => window.location.assign(url),
  });

  const createFolder = useMutation({
    mutationFn: (name: string) => api.createProject({ name, parentId: selectedFolder }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  const renameFolder = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.renameProject(input.id, input.name),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  const deleteFolder = useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: (_r, id) => {
      if (selectedFolder === id) setSelectedFolder(null);
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
    },
  });

  const sensors = useSensors(
    // A small activation distance, so a click opens the video rather than
    // starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = (recordings.data ?? [])
      .filter((r) =>
        selectedFolder === null ? true : r.projectId === selectedFolder,
      )
      .filter((r) => (query ? r.title.toLowerCase().includes(query) : true));

    const by: Record<SortKey, (a: RecordingSummary, b: RecordingSummary) => number> = {
      newest: (a, b) => +new Date(b.startedAt) - +new Date(a.startedAt),
      oldest: (a, b) => +new Date(a.startedAt) - +new Date(b.startedAt),
      longest: (a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0),
      name: (a, b) => a.title.localeCompare(b.title),
    };
    return [...filtered].sort(by[sort]);
  }, [recordings.data, selectedFolder, search, sort]);

  function onDragStart(event: DragStartEvent) {
    const data = event.active.data.current as { id: string; label: string } | undefined;
    if (data) setDragging(data);
  }

  function onDragEnd(event: DragEndEvent) {
    setDragging(null);
    const { active, over } = event;
    if (!over) return;
    const source = active.data.current as { id: string } | undefined;
    if (!source) return;

    const overId = String(over.id);
    const targetId = overId === "root" ? null : overId.replace(/^folder:/, "");
    moveRecording.mutate({ id: source.id, projectId: targetId });
  }

  const folderNameOf = (id: string | null) =>
    id === null ? null : (folders.data?.find((f) => f.id === id)?.name ?? null);

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-text">{t("title")}</h1>
          <p className="mt-0.5 text-[13px] text-text-muted">
            {t("intro")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <UploadButton projectId={selectedFolder} />
          <ImportLinkButton projectId={selectedFolder} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[13px] font-semibold text-text">{t("folders.heading")}</h2>
            <Button
              variant="ghost"
              size="icon"
              title={t("folders.new")}
              aria-label={t("folders.new")}
              onClick={() => setCreatingFolder(true)}
            >
              <FolderPlus size={15} />
            </Button>
          </div>

          {creatingFolder ? (
            <form
              className="mb-2 flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                const name = folderName.trim();
                if (name) createFolder.mutate(name);
                setCreatingFolder(false);
                setFolderName("");
              }}
            >
              <input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onBlur={() => setCreatingFolder(false)}
                placeholder={t("folders.namePlaceholder")}
                autoFocus
                className="h-8 min-w-0 flex-1 rounded-lg border border-accent bg-bg px-2.5 text-[13px] text-text placeholder:text-text-faint focus:outline-none"
              />
            </form>
          ) : null}

          <FolderTree
            folders={folders.data ?? []}
            selectedId={selectedFolder}
            onSelect={setSelectedFolder}
            draggingId={null}
            onRename={(id, name) => renameFolder.mutate({ id, name })}
            onDelete={(id) => deleteFolder.mutate(id)}
          />
        </aside>

        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 basis-56">
              <Search
                size={14}
                className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-text-faint"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("toolbar.searchLibrary")}
                className="w-full rounded-lg border border-border bg-bg-subtle py-2 pr-3 pl-8 text-[13.5px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="rounded-lg border border-border bg-bg-subtle px-2.5 py-2 text-[13px] text-text focus:border-accent focus:outline-none"
              title={t("sort.label")}
            >
              {SORT_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(`sort.${key}`)}
                </option>
              ))}
            </select>
          </div>

          {recordings.isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }, (_, i) => (
                <Skeleton key={i} className="aspect-[16/12] rounded-xl" />
              ))}
            </div>
          ) : recordings.isError ? (
            <ErrorState
              title={t("empty.loadLibraryFailed")}
              error={t("empty.reloadHint")}
            />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={<Clapperboard size={22} />}
              title={
                search || selectedFolder
                  ? t("empty.nothingMatches")
                  : t("empty.noVideos")
              }
              description={
                search || selectedFolder
                  ? t("empty.clearOrPick")
                  : t("empty.noVideosHint")
              }
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {visible.map((recording) => (
                <DraggableCard
                  key={recording.id}
                  recording={recording}
                  folderName={folderNameOf(recording.projectId)}
                  menuOpen={menuFor === recording.id}
                  onMenuToggle={() =>
                    setMenuFor((open) => (open === recording.id ? null : recording.id))
                  }
                  onOpen={() =>
                    void navigate({
                      to: "/r/$recordingId",
                      params: { recordingId: recording.id },
                    })
                  }
                  menu={
                    <CardMenu
                      recording={recording}
                      folders={folders.data ?? []}
                      busy={{
                        studio: editInStudio.isPending,
                        download: download.isPending,
                        delete: deleteRecording.isPending,
                      }}
                      onOpen={() =>
                        void navigate({
                          to: "/r/$recordingId",
                          params: { recordingId: recording.id },
                        })
                      }
                      onStudio={() => editInStudio.mutate(recording.id)}
                      onDownload={() => download.mutate(recording.id)}
                      onMove={(projectId) => {
                        moveRecording.mutate({ id: recording.id, projectId });
                        setMenuFor(null);
                      }}
                      onRename={(title) => {
                        renameRecording.mutate({ id: recording.id, title });
                        setMenuFor(null);
                      }}
                      onDelete={() => {
                        deleteRecording.mutate(recording.id);
                        setMenuFor(null);
                      }}
                    />
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* What travels with the pointer mid-drag. */}
      <DragOverlay>
        {dragging ? (
          <div className="rounded-lg border border-border bg-surface px-3 py-1.5 text-[12.5px] text-text shadow-[var(--shadow-md)]">
            {dragging.label}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function DraggableCard({
  recording,
  folderName,
  menuOpen,
  onMenuToggle,
  onOpen,
  menu,
}: {
  recording: RecordingSummary;
  folderName: string | null;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onOpen: () => void;
  menu: React.ReactNode;
}) {
  const { t } = useTranslation("library");
  const st = useServerText();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `recording:${recording.id}`,
    data: { type: "recording", id: recording.id, label: recording.title },
  });

  /**
   * Where the open menu sits, in viewport coordinates, clamped on-screen.
   *
   * Anchored under the kebab but never past either edge: a card-anchored
   * `right-0` menu extends left of the viewport on every left-column card at
   * phone width — the same clamp SpeakerMenu uses. Fixed positioning also
   * frees the menu from the grid cell's width entirely.
   */
  const [menuAt, setMenuAt] = useState<{ top: number; left: number } | null>(null);
  const MENU_W = 208;

  const explanation = statusExplanation(recording);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn("relative", isDragging && "opacity-50")}
      title={
        explanation
          ? st(explanation)
          : folderName
            ? t("card.inFolder", { name: folderName })
            : undefined
      }
    >
      <MediaCard
        recording={recording}
        onOpen={onOpen}
        action={
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setMenuAt({
                top: rect.bottom + 4,
                left: Math.max(8, Math.min(rect.right - MENU_W, window.innerWidth - MENU_W - 8)),
              });
              onMenuToggle();
            }}
            aria-label={t("menu.actions")}
            aria-expanded={menuOpen}
            className={cn(
              "rounded-md bg-black/55 p-1 text-white/80 transition-opacity hover:text-white",
              menuOpen ? "opacity-100" : "touch-show opacity-0 group-hover:opacity-100",
            )}
          >
            <MoreVertical size={14} />
          </button>
        }
      />

      {/* The menu lives OUTSIDE the card, as a sibling: the card and its
          poster are `overflow-hidden` (rounded corners, cover-fit image), and
          a menu inside them gets clipped to the poster's height — which made
          Rename and Delete unclickable on every card. Fixed + clamped, so it
          can be taller than the card and never leaves the viewport sideways. */}
      {menuOpen && menuAt ? (
        <div className="fixed z-30" style={{ top: menuAt.top, left: menuAt.left }}>
          {menu}
        </div>
      ) : null}
    </div>
  );
}

function CardMenu({
  recording,
  folders,
  busy,
  onOpen,
  onStudio,
  onDownload,
  onMove,
  onRename,
  onDelete,
}: {
  recording: RecordingSummary;
  folders: Array<{ id: string; name: string }>;
  busy: { studio: boolean; download: boolean; delete: boolean };
  onOpen: () => void;
  onStudio: () => void;
  onDownload: () => void;
  onMove: (projectId: string | null) => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  const [mode, setMode] = useState<"actions" | "move" | "rename" | "delete">("actions");
  const [title, setTitle] = useState(recording.title);
  const ready = recording.status === "ready";

  const item =
    "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[12.5px] text-text hover:bg-surface-hover disabled:opacity-50";

  return (
    <div
      className="w-52 rounded-lg border border-border bg-surface p-1 shadow-[var(--shadow-md)]"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {mode === "actions" ? (
        <>
          <button type="button" className={item} onClick={onOpen}>
            <Play size={13} /> {t("common:actions.open")}
          </button>
          <button
            type="button"
            className={item}
            disabled={!ready || busy.studio}
            title={ready ? undefined : t("menu.availableWhenReady")}
            onClick={onStudio}
          >
            {busy.studio ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Clapperboard size={13} />
            )}
            {t("menu.editInStudio")}
          </button>
          <button
            type="button"
            className={item}
            disabled={!ready || busy.download}
            title={ready ? undefined : t("menu.availableWhenReady")}
            onClick={onDownload}
          >
            {busy.download ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Download size={13} />
            )}
            {t("common:actions.download")}
          </button>
          <button type="button" className={item} onClick={() => setMode("move")}>
            <FolderInput size={13} /> {t("menu.moveToFolder")}
          </button>
          <button type="button" className={item} onClick={() => setMode("rename")}>
            <Pencil size={13} /> {t("menu.rename")}
          </button>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            className={cn(item, "text-danger hover:text-danger")}
            onClick={() => setMode("delete")}
          >
            <Trash2 size={13} /> {t("menu.delete")}
          </button>
        </>
      ) : mode === "move" ? (
        <>
          <p className="px-2.5 py-1 text-[11.5px] text-text-faint">{t("menu.moveTo")}</p>
          <button
            type="button"
            className={item}
            onClick={() => onMove(null)}
            disabled={recording.projectId === null}
          >
            {t("menu.noFolder")}
          </button>
          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              className={item}
              onClick={() => onMove(folder.id)}
              disabled={recording.projectId === folder.id}
            >
              {folder.name}
            </button>
          ))}
        </>
      ) : mode === "rename" ? (
        <form
          className="p-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            const next = title.trim();
            if (next && next !== recording.title) onRename(next);
          }}
        >
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            dir="auto"
            className="w-full rounded-md border border-accent bg-bg px-2 py-1.5 text-[12.5px] text-text focus:outline-none"
          />
          <div className="mt-1.5 flex justify-end gap-1">
            <button
              type="button"
              className="rounded px-2 py-1 text-[12px] text-text-muted hover:bg-surface-hover"
              onClick={() => setMode("actions")}
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              className="rounded bg-accent px-2 py-1 text-[12px] text-white"
            >
              {t("common:actions.rename")}
            </button>
          </div>
        </form>
      ) : (
        <div className="p-1.5">
          <p className="px-1 text-[12px] leading-relaxed text-text-muted">
            {t("menu.confirmDelete")}
          </p>
          <div className="px-1">
            <DeleteImpact recordingId={recording.id} />
          </div>
          <div className="mt-1.5 flex justify-end gap-1">
            <button
              type="button"
              className="rounded px-2 py-1 text-[12px] text-text-muted hover:bg-surface-hover"
              onClick={() => setMode("actions")}
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="button"
              disabled={busy.delete}
              className="rounded bg-danger px-2 py-1 text-[12px] text-white disabled:opacity-50"
              onClick={onDelete}
            >
              {t("common:actions.delete")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
